/**
 * SIM-01 Bandeau permanent · SIM-02 Panneau de simulation (identité, horloge, variantes des décisions ouvertes,
 * Sirene, boîte mail simulée, réinitialisation). N'existe qu'en mode simulation (routes /_simulation/* x-simulation-only).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Icon, SelectField, Tabs, useToast } from '@myron/design-system';
import { FlaskConical, X } from 'lucide-react';
import { LIBELLE_ROLE, type Role } from '@myron/domain';
import { get, messageErreur, post, put } from '../../shared/api/client';
import { useSession, type Session } from '../../shared/auth/session';
import { dateHeure } from '../../shared/ui/format';

export const MODE_SIMULATION = import.meta.env.VITE_API_MODE !== 'real';

interface EtatSim { horloge: string; identite?: string; variantes: Record<string, unknown>; sireneIndisponible: boolean; avertissement: string }
interface CompteSim { email: string; roles: Role[]; mfa: boolean; type: 'CLIENT' | 'INTERNE'; motDePasse: string; secretTotp: string | null }
interface EmailSim { id: string; a: string; sujet: string; corps: string; lien?: string | null; creeLe: string }

export function BandeauSimulation() {
  const [ouvert, setOuvert] = useState(false);
  const etat = useQuery({ queryKey: ['simulation', 'etat'], queryFn: () => get<EtatSim>('/_simulation/etat'), enabled: MODE_SIMULATION, refetchInterval: 30_000 });
  if (!MODE_SIMULATION) return null;
  return (
    <>
      <div className="my-bandeau-sim" role="note" data-testid="bandeau-simulation">
        <strong>Mode simulation — aucune donnée réelle, aucune protection réelle.</strong>
        {etat.data && <span>Horloge simulée : {dateHeure(etat.data.horloge)}</span>}
        <button type="button" className="my-btn my-btn--secondaire my-btn--compact" onClick={() => setOuvert(true)} aria-haspopup="dialog">
          <Icon icon={FlaskConical} /> Simulation
        </button>
      </div>
      {ouvert && <PanneauSimulation onFermer={() => setOuvert(false)} />}
    </>
  );
}

type Onglet = 'identite' | 'horloge' | 'variantes' | 'emails' | 'donnees';

function PanneauSimulation({ onFermer }: { onFermer: () => void }) {
  const [onglet, setOnglet] = useState<Onglet>('identite');
  return (
    <div className="my-scrim my-scrim--fixe" onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <aside className="my-tiroir" role="dialog" aria-modal="true" aria-labelledby="titre-sim" onKeyDown={(e) => { if (e.key === 'Escape') onFermer(); }}>
        <div className="my-ligne my-ligne--espace">
          <h2 id="titre-sim" className="my-titre-3">Simulation</h2>
          <Button variante="tertiaire" compact icone={X} onClick={onFermer} aria-label="Fermer le panneau de simulation">Fermer</Button>
        </div>
        <Alert ton="info">Outils de démonstration. Ils n’existent pas dans la version de production.</Alert>
        <Tabs<Onglet>
          label="Outils de simulation"
          actif={onglet}
          onChange={setOnglet}
          onglets={[{ id: 'identite', libelle: 'Identité' }, { id: 'horloge', libelle: 'Horloge' }, { id: 'variantes', libelle: 'Décisions' }, { id: 'emails', libelle: 'E-mails' }, { id: 'donnees', libelle: 'Données' }]}
        />
        <div role="tabpanel">
          {onglet === 'identite' && <OngletIdentite onFermer={onFermer} />}
          {onglet === 'horloge' && <OngletHorloge />}
          {onglet === 'variantes' && <OngletVariantes />}
          {onglet === 'emails' && <OngletEmails onFermer={onFermer} />}
          {onglet === 'donnees' && <OngletDonnees onFermer={onFermer} />}
        </div>
      </aside>
    </div>
  );
}

function OngletIdentite({ onFermer }: { onFermer: () => void }) {
  const comptes = useQuery({ queryKey: ['simulation', 'comptes'], queryFn: () => get<CompteSim[]>('/_simulation/comptes') });
  const { definir, session } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const code = useQuery({
    queryKey: ['simulation', 'mfa', session?.utilisateur.email],
    queryFn: () => get<{ code: string; expireDans: number }>('/_simulation/mfa/code', { email: session!.utilisateur.email }),
    enabled: !!session?.utilisateur.email,
    refetchInterval: 5_000,
    retry: false,
  });
  const changer = useMutation({
    mutationFn: (email: string) => post<Session>('/_simulation/identite', { email }),
    onSuccess: (s) => {
      // On purge les données de l'identité précédente sans toucher à la session qu'on vient d'obtenir.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' && q.queryKey[0] !== 'simulation' });
      definir({ ...s, etape: s.etape ?? 'CONNECTE' });
      onFermer();
      nav(s.etape === 'ENROLEMENT_MFA_REQUIS' ? '/connexion/enrolement-mfa' : s.utilisateur.type === 'INTERNE' ? '/admin' : '/pro');
    },
  });
  return (
    <div className="my-pile">
      {session && (
        <div className="my-pile my-pile--serre">
          <p><strong>Connecté :</strong> {session.utilisateur.email}</p>
          {code.data && <p>Code MFA courant : <code className="my-code" data-testid="code-mfa-simule">{code.data.code}</code> <span className="my-legende">({code.data.expireDans} s)</span></p>}
        </div>
      )}
      <p className="my-petit my-secondaire">Mot de passe commun des comptes fictifs : <code className="my-code">Simulation-2026!</code></p>
      {changer.isError && <Alert ton="erreur">{messageErreur(changer.error)}</Alert>}
      {(['INTERNE', 'CLIENT'] as const).map((t) => (
        <section key={t} className="my-pile my-pile--serre">
          <h3 className="my-titre-4">{t === 'INTERNE' ? 'Personnel MYRON' : 'Clients professionnels'}</h3>
          <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(comptes.data ?? []).filter((c) => c.type === t).map((c) => (
              <li key={c.email} className="my-ligne my-ligne--espace">
                <span className="my-petit">{c.email}<br /><span className="my-secondaire">{c.roles.map((r) => LIBELLE_ROLE[r]).join(', ')}{c.mfa ? ' · MFA' : ''}</span></span>
                <Button compact variante="secondaire" chargement={changer.isPending && changer.variables === c.email} onClick={() => changer.mutate(c.email)} aria-label={`Prendre l'identité ${c.email}`}>Utiliser</Button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function OngletHorloge() {
  const qc = useQueryClient();
  const toast = useToast();
  const etat = useQuery({ queryKey: ['simulation', 'etat'], queryFn: () => get<EtatSim>('/_simulation/etat') });
  const m = useMutation({
    mutationFn: (corps: object) => post<EtatSim>('/_simulation/horloge', corps),
    onSuccess: (e) => { qc.setQueryData(['simulation', 'etat'], e); void qc.invalidateQueries(); toast({ ton: 'info', titre: 'Horloge simulée', texte: dateHeure(e.horloge) }); },
  });
  return (
    <div className="my-pile">
      <p>Heure simulée : <strong data-testid="horloge-simulee">{etat.data ? dateHeure(etat.data.horloge) : '…'}</strong></p>
      <p className="my-petit my-secondaire">Avancer l’horloge permet de vérifier l’expiration des sessions, des commandes non payées, des validations et la rétention.</p>
      <div className="my-ligne">
        {[[16, '+16 min'], [61, '+61 min'], [24 * 60, '+1 jour'], [73 * 60, '+73 h']].map(([min, l]) => (
          <Button key={String(l)} compact variante="secondaire" onClick={() => m.mutate({ avancerMinutes: min })}>{l}</Button>
        ))}
        <Button compact variante="tertiaire" onClick={() => m.mutate({ reinitialiser: true })}>Revenir à l’heure de référence</Button>
      </div>
    </div>
  );
}

function OngletVariantes() {
  const qc = useQueryClient();
  const etat = useQuery({ queryKey: ['simulation', 'etat'], queryFn: () => get<EtatSim>('/_simulation/etat') });
  const m = useMutation({ mutationFn: (v: object) => put<EtatSim>('/_simulation/variantes', v), onSuccess: (e) => qc.setQueryData(['simulation', 'etat'], e) });
  const s = useMutation({ mutationFn: (mode: string) => post<EtatSim>('/_simulation/siret', { mode }), onSuccess: (e) => qc.setQueryData(['simulation', 'etat'], e) });
  const v = (etat.data?.variantes ?? {}) as Record<string, string | boolean>;
  const sel = (cle: string, label: string, options: [string, string][]) => (
    <SelectField label={label} value={String(v[cle] ?? options[0]![0])} onChange={(e) => m.mutate({ [cle]: e.target.value })} options={options.map(([value, l]) => ({ value, label: l }))} />
  );
  return (
    <div className="my-pile">
      <p className="my-petit my-secondaire">Points métier non arbitrés : chaque variante est simulée pour comparaison. Aucune n’est une règle définitive.</p>
      {sel('varianteRetrait', 'DO-01 — CB au retrait', [['R-A', 'R-A strict : préparation au guichet'], ['R-B', 'R-B : préparation sans réservation'], ['R-C', 'R-C : acompte en ligne']])}
      {sel('varianteVirement', 'DO-02 — Virement', [['V-B', 'V-A + V-B : strict avec avertissement'], ['V-A', 'V-A : strict'], ['V-C', 'V-C : pré-réservation (contraire à la règle validée, comparaison)']])}
      {sel('traitementStockInsuffisant', 'DO-04 — Payée sans stock', [['CHOIX_CLIENT', 'Choix laissé au client'], ['PARTIEL_ET_REMBOURSEMENT', 'Partiel + remboursement'], ['ATTENTE_REAPPRO', 'Attente réapprovisionnement'], ['ANNULATION_TOTALE', 'Annulation totale']])}
      {sel('disponibiliteVisiteur', 'DO-12 — Disponibilité visible des visiteurs', [['QUALITATIVE', 'Qualitative'], ['MASQUEE', 'Masquée']])}
      <SelectField
        label="Contrôles SIRET (API Sirene simulée)"
        value={etat.data?.sireneIndisponible ? 'INDISPONIBLE' : 'NORMAL'}
        onChange={(e) => s.mutate(e.target.value)}
        options={[{ value: 'NORMAL', label: 'Disponible' }, { value: 'INDISPONIBLE', label: 'Indisponible (dossier à vérifier)' }]}
      />
      <p className="my-legende"><Badge ton="info">Décision validée</Badge> Activation automatique des comptes après contrôles conformes : paramètre <code className="my-code">inscription.validationAuto</code>.</p>
    </div>
  );
}

function OngletEmails({ onFermer }: { onFermer: () => void }) {
  const nav = useNavigate();
  const emails = useQuery({ queryKey: ['simulation', 'emails'], queryFn: () => get<EmailSim[]>('/_simulation/emails'), refetchInterval: 5_000 });
  return (
    <div className="my-pile">
      <p className="my-petit my-secondaire">Boîte de réception simulée : aucun e-mail n’est réellement envoyé.</p>
      {emails.data?.length === 0 && <p>Aucun e-mail.</p>}
      <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }} data-testid="emails-simules">
        {(emails.data ?? []).map((m) => (
          <li key={m.id} className="my-card my-card--pad my-pile my-pile--serre">
            <span className="my-legende my-secondaire">{dateHeure(m.creeLe)} · à {m.a}</span>
            <strong>{m.sujet}</strong>
            <span className="my-petit" style={{ whiteSpace: 'pre-wrap' }}>{m.corps}</span>
            {m.lien && <Button compact variante="secondaire" onClick={() => { onFermer(); nav(m.lien!); }}>Ouvrir le lien</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function OngletDonnees({ onFermer }: { onFermer: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { definir } = useSession();
  const m = useMutation({
    mutationFn: () => post<EtatSim>('/_simulation/reinitialiser'),
    onSuccess: () => { qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' }); definir(null); onFermer(); nav('/'); },
  });
  return (
    <div className="my-pile">
      <p>Remet toutes les données fictives à leur état initial et déconnecte la session.</p>
      {m.isError && <Alert ton="erreur">{messageErreur(m.error)}</Alert>}
      <Button variante="danger" chargement={m.isPending} onClick={() => m.mutate()}>Réinitialiser les données de simulation</Button>
    </div>
  );
}
